import PropTypes from 'prop-types';
import styled from 'styled-components';
import Link from 'next/link';

import { config } from '_config';
import { theme } from '_constants/theme';

import * as Layout from '_components/atomic/layouts';
import JoinBanner from '../JoinBanner';

export const understandingTheDifferentTypesOfBusinessEntitiesContents = [
  'Sole Proprietorship',
  'Partnership',
  'Limited Liability Company (LLC)',
  'Corporation',
  'Non Profit Organization',
  'Cooperative',
];

const StyledContainer = styled.div`
  position: relative;
`;

const StyledBlock = styled.div`
  position: relative;
  z-index: 1;

  ul {
    padding-top: ${theme.spaces.regular}px;

    li {
      margin-bottom: ${theme.spaces.regular}px;

      b {
        text-decoration: underline;
      }
    }
  }
`;

const StyledBackgroundImageContainer = styled.div`
  position: absolute;
  top: 50%;
  width: 100%;

  img {
    width: 100%;
  }
`;

const UnderstandingTheDifferentTypesOfBusinessEntitiesArticle = ({
  idPrefix,
}) => {
  return (
    <StyledContainer>
      <Layout.Card.Text>
        Choosing the right business entity is a crucial decision for any
        entrepreneur. The type of entity you select affects everything from your
        personal liability to how you pay taxes and manage your business. This
        comprehensive guide will explore the different types of business
        entities, including their advantages, disadvantages, and ideal scenarios
        for each. By the end of this article, you&apos;ll have a clear
        understanding of which entity best suits your business needs.
      </Layout.Card.Text>
      <StyledBlock id={`${idPrefix}-${0}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Sole Proprietorship
        </Layout.Card.Text>
        <Layout.Card.Text>
          A sole proprietorship is the simplest and most common form of business
          entity. It is owned and operated by one individual, with no
          distinction between the owner and the business. This means the owner
          is personally responsible for all business debts and obligations.
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Advantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Easy to Form: Minimal paperwork and legal formalities are
              required.
            </li>
            <li>
              Complete Control: The owner makes all decisions and retains all
              profits.
            </li>
            <li>
              Tax Benefits: Business income is reported on the owner&apos;s
              personal tax return, simplifying tax filing.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Disadvantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Unlimited Liability: The owner is personally liable for all
              business debts and legal actions.
            </li>
            <li>
              Limited Capital: Raising funds can be challenging, as it relies on
              the owner&apos;s personal resources.
            </li>
            <li>
              Lack of Continuity: The business ceases to exist if the owner
              retires, becomes incapacitated, or dies.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Sole Proprietors are Ideal for
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>Small businesses with low risk.</li>
            <li>
              Entrepreneurs looking to test a business idea before committing
              more resources.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${1}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Partnership
        </Layout.Card.Text>
        <Layout.Card.Text>
          A partnership involves two or more people who share ownership of a
          business. There are <b>several types</b> of partnerships, including
          general partnerships (GP), limited partnerships (LP), and limited
          liability partnerships (LLP).
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" size={theme.fontSizes.large} boldest>
          General Partnership (GP)
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Advantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Easy to Form: Similar to sole proprietorship, with minimal
              formalities.
            </li>
            <li>
              Shared Resources: Partners can pool their skills, knowledge, and
              financial resources.
            </li>
            <li>
              Tax Benefits: Profits and losses are passed through to
              partners&apos; personal tax returns.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Disadvantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Unlimited Liability: Partners are personally liable for business
              debts and obligations.
            </li>
            <li>
              Shared Control: Disagreements between partners can affect business
              operations.
            </li>
            <li>
              Limited Continuity: The partnership may dissolve if a partner
              leaves or dies.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" size={theme.fontSizes.large} boldest>
          Limited Partnership (LP)
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Advantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Limited Liability: Limited partners have liability only up to the
              amount of their investment
            </li>
            <li>
              Attractive to Investors: Provides a structure for passive
              investors.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Disadvantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Complex Formation: Requires more legal documentation and
              formalities.
            </li>
            <li>
              General Partner Liability: General partners still have unlimited
              liability.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" size={theme.fontSizes.large} boldest>
          Limited Liability Partnership (LLP)
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Advantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Limited Liability: Partners are not personally liable for the
              misconduct or negligence of other partners.
            </li>
            <li>
              Flexibility: Combines elements of partnerships and corporations.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Disadvantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Varies by State: LLP regulations and protections can vary
              significantly by state.
            </li>
            <li>
              Complex Management: Requires detailed partnership agreements and
              ongoing compliance.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Partnerships are Ideal for
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Professional groups such as law firms, accounting firms, and
              consulting firms.
            </li>
            <li>
              Businesses looking to leverage the skills and resources of
              multiple owners.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${2}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Limited Liability Company (LLC)
        </Layout.Card.Text>
        <Layout.Card.Text>
          An LLC is a hybrid business entity that combines the limited liability
          protection of a corporation with the tax benefits and operational
          flexibility of a partnership. LLC owners are called members and can
          include individuals, corporations, and other LLCs.
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Advantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Limited Liability: Members are not personally liable for business
              debts and obligations.
            </li>
            <li>
              Tax Flexibility: Can choose to be taxed as a sole proprietorship,
              partnership, S corporation, or C corporation.
            </li>
            <li>
              Operational Flexibility: Fewer formalities and regulations
              compared to corporations.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Disadvantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Formation Costs: More expensive to form than a sole proprietorship
              or partnership.
            </li>
            <li>
              State-Specific Regulations: LLC laws vary by state, which can
              affect operations.
            </li>
            <li>
              Self-Employment Taxes: Members may have to pay self-employment
              taxes on their share of the profits.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Limited Liability Companies (LLC) are Ideal for
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Small to medium-sized businesses looking for liability protection
              and tax flexibility.
            </li>
            <li>Businesses with multiple owners or investors.</li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${3}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Corporations
        </Layout.Card.Text>
        <Layout.Card.Text>
          A corporation is a separate legal entity owned by shareholders. There
          are two main types of corporations: C corporations (C corp) and S
          corporations (S corp).
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" size={theme.fontSizes.large} boldest>
          C Corporation (C Corp)
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Advantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Limited Liability: Shareholders are not personally liable for
              business debts.
            </li>
            <li>
              Unlimited Growth Potential: Can raise capital through the sale of
              stock.
            </li>
            <li>
              Tax Benefits: Can deduct the cost of benefits provided to
              employees.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Disadvantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Double Taxation: Profits are taxed at the corporate level and
              again as shareholder dividends.
            </li>
            <li>
              Complex Formation and Compliance: Requires significant paperwork,
              including articles of incorporation, bylaws, and regular meetings.
            </li>
            <li>
              Regulatory Requirements: Subject to extensive regulations and
              reporting requirements.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" size={theme.fontSizes.large} boldest>
          S Corporation (S Corp)
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Advantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Pass-Through Taxation: Profits and losses pass through to
              shareholders&apos; personal tax returns, avoiding double taxation.
            </li>
            <li>
              Limited Liability: Shareholders are not personally liable for
              business debts
            </li>
            <li>
              Tax Benefits: Certain tax deductions and credits are available.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Disadvantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Eligibility Requirements: Must meet specific IRS criteria,
              including a limit on the number and type of shareholders.
            </li>
            <li>
              Operational Restrictions: More formalities than an LLC, such as
              regular board meetings and maintaining corporate minutes.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Corporations are Ideal for
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Businesses planning to raise capital through stock issuance.
            </li>
            <li>
              Companies seeking liability protection and the ability to attract
              investors.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${4}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Nonprofit Organization
        </Layout.Card.Text>
        <Layout.Card.Text>
          A nonprofit organization is a business entity formed for purposes
          other than generating profit. Nonprofits often focus on social,
          educational, religious, or charitable activities. They can apply for
          tax-exempt status under Section 501(c)(3) of the Internal Revenue
          Code.
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Advantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Tax-Exempt Status: Exempt from federal and state income taxes on
              earnings related to their exempt purposes.
            </li>
            <li>Grant Eligibility: Can apply for grants and public funding.</li>
            <li>
              Limited Liability: Directors and officers are not personally
              liable for the nonprofit&apos;s debts.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Disadvantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Strict Compliance Requirements: Must adhere to federal and state
              regulations, including detailed record-keeping and reporting.
            </li>
            <li>
              Limited Profit Distribution: Profits cannot be distributed to
              members or directors but must be reinvested in the
              organization&apos;s mission.
            </li>
            <li>
              Complex Formation: Requires significant paperwork, including
              articles of incorporation and an application for tax-exempt
              status.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Non Profit Organizations are Ideal for
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Organizations focused on charitable, educational, religious, or
              social missions.
            </li>
            <li>
              Groups seeking to operate as a tax-exempt entity to receive
              donations and grants.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${5}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Cooperative
        </Layout.Card.Text>
        <Layout.Card.Text>
          A cooperative, or co-op, is a business owned and operated by a group
          of individuals for their mutual benefit. Members of a cooperative
          share profits and decision-making responsibilities.
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Advantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Member Control: Members have equal voting rights, regardless of
              their investment.
            </li>
            <li>
              Profit Distribution: Profits are distributed among members based
              on their participation.
            </li>
            <li>
              Limited Liability: Members are not personally liable for business
              debts.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Disadvantages
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Limited Capital: Raising capital can be challenging as it relies
              on member contributions.
            </li>
            <li>
              Complex Management: Requires a high level of member participation
              and consensus in decision-making.
            </li>
            <li>
              Regulatory Requirements: Subject to specific state and federal
              regulations.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text boldest noBottomSpace>
          Cooperatives are Ideal for
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ul>
            <li>
              Groups seeking to pool resources for mutual benefit, such as
              agricultural, retail, or housing cooperatives.
            </li>
            <li>
              Businesses prioritizing democratic control and member
              participation.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Card.Text>
        Choosing the right business entity is a critical decision that impacts
        your liability, taxes, and overall business operations. Whether
        you&apos;re a solo entrepreneur starting a small business or a group of
        professionals looking to form a partnership, understanding the different
        types of business entities is essential.
      </Layout.Card.Text>
      <Layout.Card.Text>
        By considering the advantages and disadvantages of each entity type, you
        can select the one that best aligns with your business goals and needs.
      </Layout.Card.Text>
      <Layout.Card.Text>
        For expert guidance and assistance in forming your business entity,
        visit <Link href="/">Incorpified</Link>. Our team of professionals will
        help you navigate the process and ensure your business is set up for
        success.
      </Layout.Card.Text>
      <JoinBanner />
      <StyledBackgroundImageContainer>
        <picture>
          <source
            srcSet={`${config.basePath}/images/blog-arrow-logo2.svg`}
            type="image/svg+xml"
          />
          <img
            src={`${config.basePath}/images/blog-arrow-logo2.svg`}
            alt="Background Logo"
          />
        </picture>
      </StyledBackgroundImageContainer>
    </StyledContainer>
  );
};

UnderstandingTheDifferentTypesOfBusinessEntitiesArticle.propTypes = {
  idPrefix: PropTypes.string,
};

export default UnderstandingTheDifferentTypesOfBusinessEntitiesArticle;
